import api from './axios'

/**
 * Submissions API
 */
export const submissionsApi = {
    /**
     * Get submissions with filters
     */
    getAll: async (filters = {}) => {
        const params = new URLSearchParams()

        if (filters.status) params.append('status', filters.status)
        if (filters.archived !== undefined) params.append('archived', filters.archived)
        if (filters.search) params.append('search', filters.search)
        if (filters.dateFrom) params.append('date_from', filters.dateFrom)
        if (filters.dateTo) params.append('date_to', filters.dateTo)
        if (filters.page) params.append('page', filters.page)
        if (filters.perPage) params.append('per_page', filters.perPage === -1 ? 10000 : filters.perPage)

        const response = await api.get(`/submissions?${params.toString()}`)
        return response
    },

    /**
     * Get single submission
     */
    getById: async (id) => {
        const response = await api.get(`/submissions/${id}`)
        return response
    },

    /**
     * Create submission (public) - handles files with FormData including nested repeater files
     */
    create: async (formId, data) => {
        // Helper: Check if value is a File object
        const isFile = (val) => val instanceof File

        // Helper: Recursively check if any files exist in data (including nested arrays/objects)
        const hasFilesDeep = (obj) => {
            if (!obj || typeof obj !== 'object') return false
            if (isFile(obj)) return true
            if (Array.isArray(obj)) {
                return obj.some(item => hasFilesDeep(item))
            }
            return Object.values(obj).some(val => hasFilesDeep(val))
        }

        // Helper: Recursively extract files and append to FormData with proper keys
        const appendFilesToFormData = (formData, obj, prefix = '') => {
            if (!obj || typeof obj !== 'object') return obj

            if (isFile(obj)) {
                // This is a file, append it directly
                formData.append(prefix, obj)
                return null // Return null so the data structure has null instead of File
            }

            if (Array.isArray(obj)) {
                return obj.map((item, index) => {
                    const key = prefix ? `${prefix}[${index}]` : `[${index}]`
                    return appendFilesToFormData(formData, item, key)
                })
            }

            // Object
            const result = {}
            for (const [key, value] of Object.entries(obj)) {
                const newKey = prefix ? `${prefix}[${key}]` : key
                result[key] = appendFilesToFormData(formData, value, newKey)
            }
            return result
        }

        const hasFiles = hasFilesDeep(data)

        if (hasFiles) {
            // Use FormData for file uploads
            const formData = new FormData()
            formData.append('form_id', formId)

            // Process data - extract files and append them, get clean data structure
            const cleanedData = appendFilesToFormData(formData, data, '')

            // Append the cleaned data (without File objects) as JSON
            formData.append('data', JSON.stringify(cleanedData))

            const response = await api.post('/submissions', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            })
            return response
        } else {
            // Regular JSON submission
            const response = await api.post('/submissions', {
                form_id: formId,
                data,
            })
            return response
        }
    },

    /**
     * Update submission status
     */
    updateStatus: async (id, status) => {
        const response = await api.put(`/submissions/${id}/status`, { status })
        return response
    },

    /**
     * Archive submission
     */
    archive: async (id) => {
        const response = await api.post(`/submissions/${id}/archive`)
        return response
    },

    /**
     * Restore submission from archive
     */
    restore: async (id) => {
        const response = await api.post(`/submissions/${id}/restore`)
        return response
    },

    /**
     * Delete submission
     */
    delete: async (id) => {
        const response = await api.delete(`/submissions/${id}`)
        return response
    },

    /**
     * Global search across all submissions (archived + active) with preview
     */
    search: async (query) => {
        const response = await api.get(`/submissions/search?q=${encodeURIComponent(query)}`)
        return response
    },

    /**
     * Get staff notes for a submission
     */
    getNotes: async (submissionId) => {
        const response = await api.get(`/submissions/${submissionId}/notes`)
        return response
    },

    /**
     * Add a staff note to a submission
     */
    addNote: async (submissionId, note) => {
        const response = await api.post(`/submissions/${submissionId}/notes`, { note })
        return response
    },

    /**
     * Delete a staff note
     */
    deleteNote: async (submissionId, noteId) => {
        const response = await api.delete(`/submissions/${submissionId}/notes/${noteId}`)
        return response
    },
}

export default submissionsApi
